import { useDomainStore } from "../store/domain-store";
import axios from "axios";
import { useSnackbar } from "../components/snackbar";
import {
    instructorChangePasswordPayload,
    instructorPersonalDetailsUpdatePayload,
    Instructor,
} from "../models/instructor";
import { Hub, updateHubPayload } from "../models/hub";
import { BankAccount, createBankAccountPayload } from "../models/account";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

export const useDomainActions = () => {
    const { showMessage } = useSnackbar();

    const navigate = useNavigate();

    const {
        setInstructor,
        setHub,
        setBankAccount,
        instructor,
        hub,
        bank_account,
    } = useDomainStore();

    const handleApiError = (error: any) => {
        
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };


    const getInstructor = async () => {
        try {
            const response = await axios.get<Instructor>(
                `${API_URL}/instructor`,
                { withCredentials: true }
            );

            setInstructor({ ...response.data, password: "", password_repeat: "" });
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateInstructor = async (instructor: Instructor, action: number) => {
        const url = action === 0 ? `${API_URL}/instructor/profile` : `${API_URL}/instructor/password`

        const payload =
            action === 0
                ? instructorPersonalDetailsUpdatePayload(instructor)
                : instructorChangePasswordPayload(instructor);

        try {
            const response = await axios.put<Instructor>(
                url,
                payload, 
                { withCredentials: true }
            );

            setInstructor({ ...response.data, password: "", password_repeat: "" });
            showMessage(action === 0 ? "Profile Updated" : "Password Updated", 3);
        } catch (error: any) {
            handleApiError(error);
        }
    };

    const getHub = async () => {
        try {
            const response = await axios.get<Hub>(
                `${API_URL}/hub`,
                { withCredentials: true }
            );
            setHub(response.data);
        } catch (error) {
            handleApiError(error)
        }
    };

    const updateHub = async (hub: Hub) => {
        try {
            const response = await axios.put<Hub>(
                `${API_URL}/hub`,
                    updateHubPayload(hub),
                    { withCredentials: true }
            );
            setHub(response.data);
            showMessage("Hub Updated", 3);
        } catch(error: any) {
            handleApiError(error);
        }
    };

    const getBankAccount = async () => {
        try {
            const response = await axios.get<BankAccount | null>(
                `${API_URL}/hub/account`,
                { withCredentials: true }
            );

            if (response.data) {
                setBankAccount(response.data);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const addBankAccount = async (bank_account: BankAccount) => {
        try {
            const response = await axios.post<BankAccount>(
                `${API_URL}/hub/account`,
                createBankAccountPayload(bank_account),
                { withCredentials: true }
            );
            setBankAccount(response.data);
            showMessage("Payout Details Added", 3);
        } catch (error: any) {
            handleApiError(error);
        }
    };

    const logOutInstructor = async () => {
        try {
            await axios.delete(
                `${API_URL}/auth/instructors/logout`,
                { withCredentials: true }
            );
            window.location.href = "/sign-in";
        } catch (error: any) {
            handleApiError(error);
        }
    };

    const deleteInstructorAccount = async (password: string) => {
        try {
            await axios.delete(
                `${API_URL}/instructor}`,
                { data: {password}, withCredentials: true }
            );
            window.location.href = "/sign-in";
        } catch (error: any) {
            handleApiError(error);
        }
    };


    return {
        instructor,
        hub,
        setHub,
        bank_account,
        getInstructor,
        updateInstructor,
        getHub,
        updateHub,
        getBankAccount,
        addBankAccount,
        logOutInstructor,
        deleteInstructorAccount,
    };
};
